import { useMemo, useState } from "react";

import { X } from "lucide-react";
import { useDispatch } from "react-redux";

import { useConfirmRecordingConsent } from "@/hooks/mutations/use-transcription";
import { useUserProfile } from "@/hooks/useUserProfile";
import { showError } from "@/utils/notification";

import { Button } from "@components/ui/button";
import { Checkbox } from "@components/ui/checkbox";

import type { CallAudioShare } from "../hooks/useCallAudioShare";
import type { SessionMode } from "../types/sessionMode";
import { ConsentInfoTooltip } from "./ConsentInfoTooltip";
import {
  DEFAULT_SPEAKERS,
  SPEAKER_ROLES,
  SpeakerChecklist,
  type SpeakerHint,
  type SpeakerRole,
} from "./SpeakerChecklist";
import { TelehealthAudioSetup } from "./TelehealthAudioSetup";

type AudioRecorderProps = {
  handleClose: () => void;
  examinee_name: string;
  caseNumber: string;
  /**
   * Shared call-tab audio capture for the Telehealth tab (MDRS-268), owned
   * by the root RecordingHost so it survives scribe-tab navigation.
   */
  callAudioShare: CallAudioShare;
  /**
   * Begin the recording session (MDRS-116). Receives the checked speakers
   * paired with their role (MDRS-163 follow-on) — forwarded all the way to
   * the batch diarization pipeline once the session ends and gets
   * reprocessed, so it can identify speakers by their real names. The live
   * session then renders in the content panel — this modal is consent-only
   * and closes on start. `mode` and `callAudioTrack` (telehealth only) are
   * forwarded to useRecordingSession.start so the shared call audio is mixed
   * into transcription + the recorded blob. `micDeviceId` (from the Audio
   * Setup step's mic picker) is forwarded the same way — omitted → browser
   * default input device.
   */
  onStartRecording: (
    speakerHints: SpeakerHint[],
    mode: SessionMode,
    callAudioTrack?: MediaStreamTrack,
    micDeviceId?: string
  ) => void | Promise<void>;
  /** True while the session handshake is in flight (Start Recording disabled). */
  isConnecting?: boolean;
};

type WizardStep = "audio" | "consent";

/**
 * The Consent Confirmation gate (MDRS-115, kept as a modal in Phase 3).
 * Two steps (MDRS-303/MDRS-268 design) — "Audio Setup" (In person/Telehealth
 * switch, mic picker + level meter, and telehealth's "Share the call tab"
 * gate) then "Consent Confirmation" (consent card + speaker checklist). On
 * confirm it hands the checked speakers' `speakerHints` to the owner, which
 * starts the session and moves the LIVE view into the content panel
 * (MDRS-116). The live recording box previously here now lives in
 * `ScribeSession`.
 */
export const AudioRecorder = ({
  handleClose,
  examinee_name,
  caseNumber,
  callAudioShare,
  onStartRecording,
  isConnecting = false,
}: AudioRecorderProps) => {
  const dispatch = useDispatch();
  const confirmRecordingConsent = useConfirmRecordingConsent();
  const [consentConfirmed, setConsentConfirmed] = useState(false);
  const [step, setStep] = useState<WizardStep>("audio");
  // In person vs telehealth. Telehealth additionally requires the call
  // tab's audio to be shared before Continue unlocks.
  const [sessionMode, setSessionMode] = useState<SessionMode>("in_person");
  const [micDeviceId, setMicDeviceId] = useState<string | undefined>(undefined);
  const isTelehealth = sessionMode === "telehealth";
  const isCallShared = callAudioShare.status === "sharing";
  const [shareError, setShareError] = useState<string | null>(null);
  const disabled = isConnecting || confirmRecordingConsent.isPending;

  // Continue (Step 1 → 2) is blocked on telehealth until a tab is shared —
  // otherwise the doctor could start a "telehealth" session that's silently
  // mic-only with no indication anything is missing.
  const canContinue = !isTelehealth || isCallShared;

  const handleToggleShare = async () => {
    setShareError(null);
    try {
      await callAudioShare.share();
    } catch (err) {
      // The picker throws on user cancellation too — that's not a real error,
      // just leave the card in its previous (idle/lost) state silently.
      if (err instanceof DOMException && err.name === "NotAllowedError") return;
      setShareError(
        err instanceof Error
          ? err.message
          : "Couldn't share the call tab. Try again."
      );
    }
  };

  // Switching away from Telehealth abandons any live share — nothing
  // downstream will use it (handleStart only forwards the track when
  // `isTelehealth` is still true), so leaving the browser's "sharing this
  // tab" indicator up for the rest of the modal's life would be a real,
  // user-visible leak, not just an internal one.
  const handleModeChange = (next: SessionMode) => {
    if (next !== "telehealth" && isCallShared) {
      callAudioShare.stop();
    }
    setSessionMode(next);
  };

  // Which speaker roles are present (MDRS-115) — feeds `speakerHints` below.
  const [speakers, setSpeakers] =
    useState<Record<SpeakerRole, boolean>>(DEFAULT_SPEAKERS);

  // The examiner is the logged-in user, so the Doctor row defaults to their
  // name; the Examinee row defaults to the case's examinee. Both fall back to
  // the role word. These are only the DEFAULTS — the user can retype any of
  // them before starting (e.g. naming the Interpreter/Other role, or fixing a
  // wrong profile name) via SpeakerChecklist's now-editable name field.
  const userProfile = useUserProfile();
  const defaultSpeakerLabels: Record<SpeakerRole, string> = useMemo(
    () => ({
      Doctor: userProfile?.name || "Doctor",
      Examinee: examinee_name || "Examinee",
      Interpreter: "Interpreter",
      Other: "Other",
    }),
    [userProfile?.name, examinee_name]
  );

  // User-editable speaker names. Only edited fields live here — a role is
  // "touched" iff it has a key in this map, so an untouched field instead
  // tracks `defaultSpeakerLabels` live (e.g. the profile/case fetch landing
  // after mount) via `speakerNames` below, with no need to copy the default
  // into state and reconcile it after the fact; once a role is edited it's
  // frozen against further default changes so a manual edit is never
  // clobbered.
  const [editedSpeakerNames, setEditedSpeakerNames] = useState<
    Partial<Record<SpeakerRole, string>>
  >({});

  const handleNameChange = (role: SpeakerRole, value: string) => {
    setEditedSpeakerNames((prev) => ({ ...prev, [role]: value }));
  };

  const speakerNames: Record<SpeakerRole, string> = useMemo(
    () =>
      SPEAKER_ROLES.reduce(
        (acc, role) => {
          acc[role] = editedSpeakerNames[role] ?? defaultSpeakerLabels[role];
          return acc;
        },
        {} as Record<SpeakerRole, string>
      ),
    [editedSpeakerNames, defaultSpeakerLabels]
  );

  // Checked speakers paired with their role (MDRS-163 follow-on) — the SAME
  // names rendered in the checklist below (user-edited or defaulted
  // doctor/examinee/interpreter/other names), in the checklist's fixed order
  // (Doctor, Examinee, Interpreter, Other). A blank/whitespace-only edit falls
  // back to the role's default. This is the single source of truth: forwarded
  // to the batch diarization pipeline for real-name speaker identification
  // once the session ends.
  const speakerHints: SpeakerHint[] = useMemo(
    () =>
      SPEAKER_ROLES.filter((role) => speakers[role]).map((role) => {
        const trimmed = speakerNames[role]?.trim();
        return { role, name: trimmed || defaultSpeakerLabels[role] };
      }),
    [speakers, speakerNames, defaultSpeakerLabels]
  );

  // Record consent server-side BEFORE starting the session (SCRIBE-02) — the
  // streaming-token endpoints reject the recording otherwise. Keep the modal
  // open on failure so the user can retry rather than silently proceeding
  // into a session that will fail to obtain a token.
  const handleStart = async () => {
    try {
      await confirmRecordingConsent.mutateAsync(caseNumber);
    } catch (err) {
      showError(
        err instanceof Error
          ? err.message
          : "Failed to confirm recording consent. Please try again.",
        dispatch
      );
      return;
    }
    try {
      await onStartRecording(
        speakerHints,
        sessionMode,
        isTelehealth ? (callAudioShare.track ?? undefined) : undefined,
        micDeviceId
      );
    } catch (error) {
      showError(
        error instanceof Error
          ? error.message
          : "Could not start recording. Please try again.",
        dispatch
      );
    }
  };

  const showAudioStep = step === "audio";

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1">
          <h2 className="text-lg font-semibold text-background-dark">
            {showAudioStep ? "Audio Setup" : "Consent Confirmation"}
          </h2>
          <ConsentInfoTooltip />
        </div>
        <div className="flex items-center gap-3.5">
          <span className="text-foreground-subtle text-[11.5px] font-medium">
            {showAudioStep ? "Step 1 of 2" : "Step 2 of 2"}
          </span>
          <button
            onClick={handleClose}
            className="text-foreground-muted hover:text-foreground"
            aria-label="Close"
          >
            <X className="size-5" />
          </button>
        </div>
      </div>

      {showAudioStep ? (
        <>
          <TelehealthAudioSetup
            mode={sessionMode}
            onModeChange={handleModeChange}
            micDeviceId={micDeviceId}
            onMicDeviceChange={setMicDeviceId}
            callAudioShare={callAudioShare}
            onToggleShare={handleToggleShare}
            shareError={shareError}
            disabled={disabled}
          />
          <div className="flex flex-col gap-2 pt-4">
            <div className="flex items-center gap-3">
              <Button
                variant="simple-outline"
                className="flex-1"
                onClick={handleClose}
              >
                Cancel
              </Button>
              <Button
                className="flex-1"
                onClick={() => canContinue && setStep("consent")}
                disabled={!canContinue}
              >
                Continue
              </Button>
            </div>
            {!canContinue && (
              <span className="text-center text-[11.5px] text-foreground-muted">
                Share the call tab, or switch to In person.
              </span>
            )}
          </div>
        </>
      ) : (
        <>
          <div className="space-y-4 rounded-lg border border-primary-100 bg-primary-50 p-4">
            <p className="text-sm text-foreground">
              You can only proceed with recording the assessment if informed consent
              has been obtained from the examinee.
            </p>
            <div className="flex items-start gap-3">
              <Checkbox
                id="consent-confirmed"
                checked={consentConfirmed}
                onCheckedChange={(checked) => setConsentConfirmed(checked === true)}
                disabled={disabled}
                className="mt-0.5"
              />
              <label
                htmlFor="consent-confirmed"
                className="cursor-pointer text-sm font-medium leading-tight"
              >
                I confirm that I have obtained the examinee&apos;s informed consent
                to use Assessment Scribe to record and transcribe this assessment.
              </label>
            </div>
          </div>

          <SpeakerChecklist
            speakers={speakers}
            labels={speakerNames}
            sessionLocked={disabled}
            onToggle={(role, checked) =>
              setSpeakers((prev) => ({ ...prev, [role]: checked }))
            }
            onNameChange={handleNameChange}
            onChangeAudio={() => setStep("audio")}
          />

          <div className="flex gap-4 pt-4">
            <Button
              variant="simple-outline"
              className="flex-1"
              onClick={() => setStep("audio")}
            >
              Go Back
            </Button>
            <Button
              className="flex-1"
              onClick={handleStart}
              isLoading={disabled}
              disabled={!consentConfirmed || speakerHints.length < 1 || disabled}
            >
              Start Recording
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
